/**
 * Cart Drawer Alpine.js Store & Components
 *
 * Consolidated from inline scripts previously in:
 *   - snippets/cart-drawer.liquid
 *   - snippets/cart-drawer-discount.liquid
 *   - snippets/cart-drawer-upsell.liquid
 *   - layout/theme.liquid
 *
 * Expects a global `window.__cartDrawerConfig` object set by a small
 * inline Liquid script before this file loads.
 */

document.addEventListener('alpine:init', () => {
  const cfg = window.__cartDrawerConfig || {};

  /* ------------------------------------------------------------------ */
  /*  Product store (was in theme.liquid)                                */
  /* ------------------------------------------------------------------ */
  Alpine.store('product', {
    selectedVariant: 'single',
    quantity: 1,
    quantityError: '',

    setVariant(variant) {
      this.selectedVariant = variant;
    },

    setQuantity(quantity, maxQuantity = null, errorMessage = '') {
      const next = parseInt(quantity, 10);
      const max =
        maxQuantity === null || maxQuantity === ''
          ? null
          : parseInt(maxQuantity, 10);

      if (max !== null && !Number.isNaN(max) && max < 1) {
        this.quantity = 1;
        this.quantityError = errorMessage;
        return false;
      }
      if (Number.isNaN(next) || next < 1) {
        this.quantity = 1;
        this.quantityError = '';
        return true;
      }
      if (max !== null && !Number.isNaN(max) && next > max) {
        this.quantity = max;
        this.quantityError = errorMessage;
        return false;
      }

      this.quantity = next;
      this.quantityError = '';
      return true;
    },

    clearQuantityError() {
      this.quantityError = '';
    },
  });

  /* ------------------------------------------------------------------ */
  /*  Product listing component (was in theme.liquid)                    */
  /* ------------------------------------------------------------------ */
  Alpine.data('productListing', () => ({
    open: false,
    loading: false,

    async addToCart(variantId, quantity = 1) {
      this.loading = true;
      try {
        await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        });
        await this.fetchCart();
      } catch (err) {
        console.error('Error:', err);
      } finally {
        this.loading = false;
      }
    },

    async fetchCart() {
      try {
        const drawer = window.Alpine && Alpine.store('cartDrawer');
        if (drawer) {
          await drawer.fetchCart();
          document.querySelectorAll('.js-cart-count').forEach((el) => {
            el.innerHTML = drawer.cart.item_count;
          });
          if (window.location.pathname !== '/cart') {
            drawer.open = true;
          }
        } else {
          const res = await fetch(window.Shopify.routes.root + 'cart.js');
          const cart = await res.json();
          document.querySelectorAll('.js-cart-count').forEach((el) => {
            el.innerHTML = cart.item_count;
          });
        }
      } catch (err) {
        console.error('Error:', err);
      }
    },
  }));

  /* ------------------------------------------------------------------ */
  /*  Cart drawer store (was in cart-drawer.liquid)                      */
  /* ------------------------------------------------------------------ */
  Alpine.store('cartDrawer', {
    open: false,
    cart: cfg.cart || { item_count: 0, items: [], total_price: 0 },
    freeShippingThreshold: cfg.freeShippingThreshold || 5000,
    freeGiftThreshold: cfg.freeGiftThreshold || 10000,
    shippingPriceCents: cfg.shippingPriceCents || 0,
    enableFreeGift: cfg.enableFreeGift || false,
    enableChoiceGift: cfg.enableChoiceGift || false,
    choiceGiftHeading: cfg.choiceGiftHeading || 'Maak je look compleet',
    freeShippingTextBefore: cfg.freeShippingTextBefore || '',
    freeShippingTextAfter: cfg.freeShippingTextAfter || '',
    freeGiftTextBefore: cfg.freeGiftTextBefore || '',
    freeGiftTextAfter: cfg.freeGiftTextAfter || '',
    returnPolicyText: cfg.returnPolicyText || '14 dagen retourneren',
    freeGiftProduct: cfg.freeGiftProduct || null,
    choiceGiftProducts: cfg.choiceGiftProducts || [],
    upsellProducts: cfg.upsellProducts || [],
    compareAtPrices: cfg.compareAtPrices || {},

    toggle() {
      this.open = !this.open;
    },

    checkout() {
      window.location.href = '/checkout';
    },

    async init() {
      await this.fetchCart();
    },

    /* ---- Formatting helpers ---- */

    formatAmount(cents) {
      return '€' + (cents / 100).toFixed(2).replace('.', ',');
    },

    formatPrice(item) {
      return this.formatAmount(item.price);
    },

    compareAtPrice(item) {
      const cap = this.compareAtPrices[item.variant_id];
      return cap && cap > item.price ? this.formatAmount(cap) : null;
    },

    discountedPrice(item) {
      if (!item.discounts || item.discounts.length === 0) return null;
      const totalDiscount = item.discounts.reduce((sum, d) => sum + d.amount, 0);
      if (totalDiscount <= 0) return null;
      return this.formatAmount(item.price - Math.round(totalDiscount / item.quantity));
    },

    /* ---- Discount helpers ---- */

    getAutoDiscounts() {
      const map = {};
      this.cart.items.forEach((item) => {
        (item.discounts || []).forEach((d) => {
          if (!map[d.title]) map[d.title] = { title: d.title, amount: 0 };
          map[d.title].amount += d.amount;
        });
      });
      return Object.values(map);
    },

    /* ---- Upsell helpers ---- */

    getVisibleUpsells() {
      const ids = this.cart.items.map((i) => i.variant_id);
      return this.upsellProducts.filter((p) => p.available && !ids.includes(p.variant_id));
    },

    /* ---- Cart mutations ---- */

    async addToCart(variantId) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      await this.fetchCart();
    },

    async updateQuantity(item, newQty) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: newQty }),
      });
      await this.fetchCart();
    },

    async removeItem(item) {
      await this.updateQuantity(item, 0);
    },

    async fetchCart() {
      const res = await fetch('/cart.js');
      this.cart = await res.json();
      await this.enrichComparePrices(this.cart.items);
      await this.handleFreeGift();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    /* ---- Choice gift helpers ---- */

    getVisibleChoiceGifts() {
      if (!this.enableChoiceGift) return [];
      return this.choiceGiftProducts.filter((p) => p.available);
    },

    isChoiceGiftInCart(variantId) {
      return this.cart.items.some(
        (i) => i.variant_id === variantId && i.properties && i.properties._free_gift
      );
    },

    async addChoiceGift(variantId) {
      if (this.isChoiceGiftInCart(variantId)) return;

      const choiceVids = this.choiceGiftProducts.map((p) => p.variant_id);
      for (const item of this.cart.items) {
        if (item.properties && item.properties._free_gift && choiceVids.includes(item.variant_id)) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
        }
      }

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties: { _free_gift: 'true' } }),
      });
      await this.fetchCart();
    },

    /* ---- Free gift auto-management ---- */

    async handleFreeGift() {
      if (!this.enableFreeGift) return;

      if (this.enableChoiceGift) {
        if (this._handlingGift) return;
        this._handlingGift = true;

        const choiceVids = this.choiceGiftProducts.map((p) => p.variant_id);
        const totalWithoutGifts = this.cart.items
          .filter((i) => !(i.properties && i.properties._free_gift))
          .reduce((sum, i) => sum + i.final_line_price, 0);

        if (totalWithoutGifts < this.freeGiftThreshold) {
          for (const item of this.cart.items) {
            if (item.properties && item.properties._free_gift && choiceVids.includes(item.variant_id)) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: item.key, quantity: 0 }),
              });
            }
          }
          const r = await fetch('/cart.js');
          this.cart = await r.json();
        }

        this._handlingGift = false;
        return;
      }

      if (!this.freeGiftProduct || !this.freeGiftProduct.available) return;
      if (this._handlingGift) return;
      this._handlingGift = true;

      const vid = this.freeGiftProduct.variant_id;
      const giftInCart = this.cart.items.find((i) => i.variant_id === vid);
      const totalWithoutGift = this.cart.items
        .filter((i) => i.variant_id !== vid)
        .reduce((sum, i) => sum + i.final_line_price, 0);
      const reached = totalWithoutGift >= this.freeGiftThreshold;

      try {
        if (reached && !giftInCart) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: vid, quantity: 1, properties: { _free_gift: 'true' } }),
          });
          const r = await fetch('/cart.js');
          this.cart = await r.json();
        } else if (!reached && giftInCart) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftInCart.key, quantity: 0 }),
          });
          const r = await fetch('/cart.js');
          this.cart = await r.json();
        }
      } catch (_) {
        /* silent */
      }

      this._handlingGift = false;
    },

    /* ---- Compare-at-price enrichment ---- */

    async enrichComparePrices(items) {
      const missing = items.filter((i) => this.compareAtPrices[i.variant_id] === undefined);
      const handles = [...new Set(missing.map((i) => i.handle))];
      await Promise.all(
        handles.map(async (handle) => {
          try {
            const res = await fetch(`/products/${handle}.js`);
            const product = await res.json();
            product.variants.forEach((v) => {
              this.compareAtPrices[v.id] = v.compare_at_price || 0;
            });
          } catch (_) {
            /* silent */
          }
        })
      );
    },

    /* ---- Threshold helpers ---- */

    getThresholdMessage() {
      const total = this.cart.total_price;
      if (total < this.freeShippingThreshold) {
        return this.freeShippingTextBefore.replace('{amount}', this.formatAmount(this.freeShippingThreshold - total));
      }
      if (!this.enableFreeGift) return this.freeShippingTextAfter;
      if (total < this.freeGiftThreshold) {
        return this.freeGiftTextBefore.replace('{amount}', this.formatAmount(this.freeGiftThreshold - total));
      }
      return this.freeGiftTextAfter;
    },

    getShippingProgress() {
      return Math.min(100, (this.cart.total_price / this.freeShippingThreshold) * 100);
    },

    getGiftProgress() {
      const range = this.freeGiftThreshold - this.freeShippingThreshold;
      return Math.min(100, Math.max(0, ((this.cart.total_price - this.freeShippingThreshold) / range) * 100));
    },

    shippingReached() {
      return this.cart.total_price >= this.freeShippingThreshold;
    },

    giftReached() {
      return this.cart.total_price >= this.freeGiftThreshold;
    },
  });

  /* ------------------------------------------------------------------ */
  /*  Cart discount component (was in cart-drawer-discount.liquid)       */
  /* ------------------------------------------------------------------ */
  Alpine.data('cartDiscount', () => ({
    code: '',
    error: '',
    success: '',
    loading: false,

    async apply() {
      if (!this.code.trim()) return;
      this.error = '';
      this.success = '';
      this.loading = true;

      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: this.code.trim() }),
        });
        const cart = await res.json();
        const submitted = this.code.trim().toLowerCase();
        const matched =
          Array.isArray(cart.discount_codes) &&
          cart.discount_codes.find((d) => d.code.toLowerCase() === submitted && d.applicable);

        if (matched) {
          await Alpine.store('cartDrawer').fetchCart();
          this.flashMessage('success', cfg.discountAppliedText || 'Kortingscode toegepast!');
          this.code = '';
        } else {
          const remaining = (cart.discount_codes || [])
            .filter((d) => d.code.toLowerCase() !== submitted)
            .map((d) => d.code)
            .join(',');
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: remaining }),
          });
          await Alpine.store('cartDrawer').fetchCart();
          this.flashMessage('error', cfg.discountErrorText || 'Ongeldige kortingscode.');
        }
      } catch (_) {
        this.flashMessage('error', cfg.discountErrorText || 'Ongeldige kortingscode.');
      } finally {
        this.loading = false;
      }
    },

    async removeCode(codeToRemove) {
      this.loading = true;
      this.error = '';
      this.success = '';

      try {
        const store = Alpine.store('cartDrawer');
        const remaining = (store.cart.discount_codes || [])
          .filter((d) => d.code.toLowerCase() !== codeToRemove.toLowerCase())
          .map((d) => d.code)
          .join(',');

        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: remaining }),
        });
        await store.fetchCart();
      } catch (_) {
        this.flashMessage('error', cfg.discountErrorText || 'Ongeldige kortingscode.');
      } finally {
        this.loading = false;
      }
    },

    flashMessage(type, msg) {
      this[type] = msg;
      setTimeout(() => { this[type] = ''; }, 2000);
    },

    formatDiscount(value, type) {
      const v = parseFloat(value);
      if (!v && v !== 0) return '';
      const abs = Math.abs(v);
      if (type === 'percentage') return `-${abs % 1 === 0 ? abs : abs.toFixed(2)}%`;
      return `-${Alpine.store('cartDrawer').formatAmount(abs)}`;
    },
  }));
});

/* ==================================================================== */
/*  Flickity – upsell carousel (was in cart-drawer.liquid)              */
/* ==================================================================== */
(function () {
  var upsellFlkty = null;

  function initFlickity(carousel) {
    if (typeof Flickity === 'undefined') {
      setTimeout(function () { initFlickity(carousel); }, 100);
      return;
    }

    if (upsellFlkty) {
      try { upsellFlkty.destroy(); } catch (_) {}
      upsellFlkty = null;
    }

    upsellFlkty = new Flickity(carousel, {
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      pageDots: false,
      prevNextButtons: false,
      freeScroll: true,
    });

    var section = carousel.closest('[data-upsell-section]');
    var bar = section && section.querySelector('.upsell-progress-fill');
    if (bar) {
      upsellFlkty.on('scroll', function (progress) {
        bar.style.width = Math.max(0, Math.min(1, progress)) * 100 + '%';
      });
    }
  }

  function tryInitFromDOM() {
    var carousel = document.querySelector('.upsell-main-carousel');
    if (carousel) {
      setTimeout(function () { initFlickity(carousel); }, 300);
    }
  }

  var observed = false;
  var observer = new MutationObserver(function () {
    if (!observed && document.querySelector('.upsell-main-carousel')) {
      observed = true;
      tryInitFromDOM();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('alpine:initialized', function () {
    setTimeout(tryInitFromDOM, 500);
  });

  document.addEventListener('cart:updated', function () {
    setTimeout(function () {
      var carousel = document.querySelector('.upsell-main-carousel');
      if (carousel) initFlickity(carousel);
    }, 500);
  });

  document.addEventListener('alpine:init', function () {
    Alpine.effect(function () {
      var store = Alpine.store('cartDrawer');
      if (store && store.open) {
        setTimeout(tryInitFromDOM, 400);
      }
    });
  });
})();

/* ==================================================================== */
/*  Upsell button bindings (was in cart-drawer-upsell.liquid)           */
/* ==================================================================== */
(function () {
  var cfg = window.__cartDrawerConfig || {};
  var addedText = '✓ ' + (cfg.upsellAddedText || 'Toegevoegd');
  var addText = cfg.upsellAddText || 'Voeg toe';

  var spinnerHtml =
    '<svg style="animation:cart-spin .7s linear infinite;display:inline-block;vertical-align:middle;" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>';

  function getStore() {
    return window.Alpine && Alpine.store('cartDrawer');
  }

  function updateButtons() {
    var store = getStore();
    if (!store) return;
    var cartVids = store.cart.items.map(function (i) { return i.variant_id; });
    document.querySelectorAll('[data-upsell-cell]').forEach(function (cell) {
      var vid = parseInt(cell.getAttribute('data-variant-id'), 10);
      var btn = cell.querySelector('[data-upsell-add-btn]');
      if (!btn) return;
      var inCart = cartVids.indexOf(vid) !== -1;
      var text = inCart ? addedText : addText;
      if (btn.textContent !== text) {
        btn.disabled = inCart;
        btn.textContent = text;
      }
    });
  }

  function bindButtons() {
    document.querySelectorAll('[data-upsell-add-btn]').forEach(function (btn) {
      if (btn._upsellBound) return;
      btn._upsellBound = true;
      btn.addEventListener('click', function () {
        var cell = btn.closest('[data-upsell-cell]');
        var vid = parseInt(cell.getAttribute('data-variant-id'), 10);
        var store = getStore();
        if (!store) return;
        btn.disabled = true;
        btn.innerHTML = spinnerHtml;
        store.addToCart(vid);
      });
    });
  }

  function init() {
    bindButtons();
    updateButtons();
  }

  document.addEventListener('cart:updated', updateButtons);

  var observer = new MutationObserver(function () {
    if (document.getElementById('upsell-slider-section')) {
      observer.disconnect();
      init();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('alpine:init', function () {
    Alpine.effect(function () {
      var store = Alpine.store('cartDrawer');
      if (store && store.open) {
        setTimeout(init, 100);
      }
    });
  });

  document.addEventListener('alpine:initialized', function () {
    setTimeout(init, 300);
  });
})();
